import { formatCurrencyCell } from '../../../../shared/format-currency.js';
import { buildAmountClause, isAmountClause, parseAmountClause, stripAmountInput } from '../../../../shared-web/utilities/filter-logic.js';
import { getCurrencyRateMap } from '../../../../shared-web/currency-rates.js';

/**
 * Operator values for amount filters.
 * @type {ReadonlyArray<string>}
 */
export const AMOUNT_OPERATORS = Object.freeze(['eq', 'gte', 'lte', 'between']);

/**
 * Build Combobox options for amount operators.
 * @param {(key: string, opts?: any) => string} t
 * @returns {Array<{ value: string, label: string }>}
 */
export function buildAmountOperatorOptions(t) {
	return [
		{ value: 'eq', label: t('filter.operator.eq') },
		{ value: 'lte', label: t('filter.operator.lte') },
		{ value: 'gte', label: t('filter.operator.gte') },
		{ value: 'between', label: t('filter.operator.between') },
	];
}

/**
 * Parse stored clause value into UI state.
 * @param {string} value
 * @returns {{ op: string, amount: string, amountEnd: string, currency: string }}
 */
export function parseAmountClauseToState(value) {
	if (!value || !isAmountClause(value)) { return { op: '', amount: '', amountEnd: '', currency: '' }; }
	const p = parseAmountClause(value);
	return {
		op: p.op ?? '',
		amount: p.amount ?? '',
		amountEnd: p.amountEnd ?? '',
		currency: p.currency ?? '',
	};
}

/**
 * Check if string is a valid amount (after stripping commas).
 * @param {string} raw
 * @returns {boolean}
 */
function isValidAmount(raw) {
	const s = stripAmountInput(raw);
	if (!s) { return false; }
	if (!/^-?\d+(\.\d+)?$/.test(s)) { return false; }
	return Number.isFinite(Number(s));
}

/**
 * Build stored clause value from UI op+amount(s)+currency.
 * @param {string} op
 * @param {string} amount
 * @param {string} [amountEnd]
 * @param {string} currency
 * @returns {string}
 */
export function buildAmountClauseValue(op, amount, amountEnd, currency) {
	if (!op || !AMOUNT_OPERATORS.includes(op)) { return ''; }
	const ccy = String(currency ?? '').trim().toUpperCase();
	if (!ccy || !/^[A-Z0-9]{2,10}$/.test(ccy)) { return ''; }
	const a = stripAmountInput(amount);
	if (!isValidAmount(a)) { return ''; }
	if (op === 'between') {
		const ae = stripAmountInput(amountEnd ?? '');
		if (!isValidAmount(ae)) { return ''; }
		return buildAmountClause(op, a, ccy, ae);
	}
	return buildAmountClause(op, a, ccy);
}

/**
 * Format a stored amount clause value for chip display.
 * @param {string} value
 * @param {(key: string, opts?: any) => string} [t]
 * @returns {string}
 */
// eslint-disable-next-line complexity
export function formatAmountChipValue(value, t) {
	if (!isAmountClause(value)) { return value; }
	const p = parseAmountClause(value);
	const ccy = p.currency ?? '';
	const map = getCurrencyRateMap();
	const meta = ccy ? map.get(ccy) : null;
	const dp = meta?.decimalPlaces;
	const sym = meta?.symbol;

	/**
	 * @param {string | null} raw
	 * @returns {string}
	 */
	function fmt(raw) {
		if (raw === null || raw === undefined) { return ''; }
		const n = Number(stripAmountInput(raw));
		if (!Number.isFinite(n)) { return String(raw); }
		if (sym !== undefined && dp !== undefined && Number.isFinite(dp)) {
			return formatCurrencyCell(n, dp, sym);
		}
		// Fallback: show raw + code
		return `${raw} ${ccy}`.trim();
	}

	if (p.op === 'eq') {
		if (t) {
			const tmpl = t('filter.operator.eqValue', { value: fmt(p.amount), currency: ccy });
			if (tmpl && tmpl !== 'filter.operator.eqValue') { return tmpl; }
		}
		return `= ${fmt(p.amount)}`;
	}
	if (p.op === 'gte') {
		// Use template if available
		if (t) {
			const tmpl = t('filter.operator.gteValue', { value: fmt(p.amount), currency: ccy });
			if (tmpl && tmpl !== 'filter.operator.gteValue') { return tmpl; }
		}
		return `≥ ${fmt(p.amount)}`;
	}
	if (p.op === 'lte') {
		if (t) {
			const tmpl = t('filter.operator.lteValue', { value: fmt(p.amount), currency: ccy });
			if (tmpl && tmpl !== 'filter.operator.lteValue') { return tmpl; }
		}
		return `≤ ${fmt(p.amount)}`;
	}
	if (p.op === 'between') {
		const a = fmt(p.amount);
		const b = fmt(p.amountEnd);
		if (t) {
			const tmpl = t('filter.operator.betweenValue', { low: a, high: b, currency: ccy });
			if (tmpl && tmpl !== 'filter.operator.betweenValue') { return tmpl; }
		}
		return `${a} – ${b}`;
	}
	return value;
}
